import {
  ButtonV3,
  HsaFsaInlineLogo,
  Price,
  TypographyV3,
} from '@jouzen/ecom-components';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';

import { usePDPContext } from '@//app/[locale]/product/_contexts/PDPProvider';
import { useWizardStateContext } from '@/app/[locale]/product/_contexts/WizardStateProvider';
import useFooterPrice from '@/app/[locale]/product/_hooks/useFooterPrice';
import StepCTA from '@/app/[locale]/product/_steps/StepCTA';
import RichText from '@/app/components/RichText';
import { useSikaPayments } from '@/app/hooks/useSikaPayments';

import AffirmPromo from '../AffirmPromo';

const FSA_HSA_FAQ_PAGE =
  'https://support.ouraring.com/hc/en-us/articles/19777137314579-Purchase-with-FSA-or-HSA-Funds';

const tValue = {
  logo: () => <HsaFsaInlineLogo width={64} className="inline" />,
  Ulink: (chunks: ReactNode) => {
    return (
      <a
        className="underline"
        href={FSA_HSA_FAQ_PAGE}
        rel="noreferrer"
        target="_blank"
      >
        {chunks}
      </a>
    );
  },
};

const Footer = () => {
  const { hasMaxChargerCount, hasMaxRingCount, itemCountForIcon } =
    useWizardStateContext();
  const { isEnabled: sikaIsEnabled } = useSikaPayments();
  const { isSuccess: isProductSuccess } = usePDPContext();
  const t = useTranslations();
  const { affirmPrice, currencyCode, discount, showAffirm, totalPrice } =
    useFooterPrice();

  return (
    <footer
      className={cx(
        'fixed inset-x-0 bottom-0 z-30 flex items-center justify-between px-4 py-2',
        'border-t border-gray-300 bg-sandstone-100',
        'lg:absolute lg:py-4',
      )}
    >
      {hasMaxChargerCount || hasMaxRingCount ? (
        <ButtonV3
          className="w-full"
          data-cy="multi-ring-max-cart"
          href="/cart"
          variant="secondary-dark"
        >
          {t('view_your_cart')}&nbsp;
          <span>{`( ${itemCountForIcon} )`}</span>
        </ButtonV3>
      ) : (
        <>
          <div
            className={cx('max-w-[45%] md:max-w-none', {
              'self-start': showAffirm,
            })}
          >
            <div
              className="text-xs font-bold lg:text-base"
              data-cy="footer-price"
            >
              {isProductSuccess && (
                <Price
                  currencyCode={currencyCode}
                  discount={discount}
                  price={totalPrice}
                  priceCompare
                  showCurrency
                  showZeroAsFree
                />
              )}
              {showAffirm && (
                <TypographyV3 className="text-xs" Element="div">
                  <AffirmPromo
                    className="text-sandstone-500"
                    pageType="product"
                    price={affirmPrice}
                  />
                </TypographyV3>
              )}
              {sikaIsEnabled && (
                <TypographyV3
                  className="text-xs"
                  data-cy="notice_hsa_fsa_eligible"
                  color="inherit"
                >
                  <RichText
                    messageKey="notice_hsa_fsa_eligible_logo_inline"
                    tValues={tValue}
                  />
                </TypographyV3>
              )}
            </div>
          </div>
          <StepCTA />
        </>
      )}
    </footer>
  );
};

export default Footer;
